import {
  CompanyPrincipalLateFeeSettingsFixed as GqlCompanyPrincipalLateFeeSettingsFixed,
  CompanyPrincipalLateFeeSettingsPercent as GqlCompanyPrincipalLateFeeSettingsPercent,
  CompanyPrincipalLateFeeType as GqlCompanyPrincipalLateFeeType,
  LoanPrincipalLateFee as GqlLoanPrincipalLateFee,
  LoanPrincipalLateFeeFixed,
  LoanPrincipalLateFeePercent,
  LoanPrincipalLateFeeType as GqlLoanPrincipalLateFeeType,
  StopSettings as GqlStopSettings,
} from '@willow/graphql-iso/src/app';
import {
  assertNever,
  CompanyPrincipalLateFeeSettings,
  LoanPrincipalLateFee,
  StopSettings,
  Temporal,
  toTemporalInstant,
} from '@willow/types-iso';

import {
  calculateFixedFee,
  calculatePercentFee,
  feeDisabledByLoanOverride,
  feeDisabledByStopSettings,
} from '../utils/feeCalculations';

const getNumDaysAfterMaturityDate = (maturityDate: Date, isAutomation: boolean): number | undefined => {
  if (isAutomation) return undefined;
  const nowPlainDate = Temporal.Now.plainDateISO('UTC');
  const maturityPlainDate = toTemporalInstant(maturityDate).toZonedDateTimeISO('UTC').toPlainDate();
  return maturityPlainDate.until(nowPlainDate, { largestUnit: 'days' }).days;
};

/**
 * @description Calculates the principal late fee amount for a loan for both client-side and server-side requests.
 * @param companyFeeSettings - The company's principal late fee settings
 * @param maturityDate - The maturity date of the loan
 * @param principal - The outstanding principal of the loan
 * @param numProperties - The number of active properties on the loan (i.e., not paid off)
 * @param loanOverrides - The loan's principal late fee overrides
 * @param stopSettings - The loan's stop settings
 */
export function getPrincipalLateFeeAmount(
  companyFeeSettings: CompanyPrincipalLateFeeSettings,
  maturityDate: Date,
  principal: number,
  numProperties: number,
  loanOverrides: LoanPrincipalLateFee | undefined,
  stopSettings: StopSettings,
  isAutomation: boolean,
): number;
export function getPrincipalLateFeeAmount(
  companyFeeSettings: GqlCompanyPrincipalLateFeeSettingsFixed | GqlCompanyPrincipalLateFeeSettingsPercent,
  maturityDate: Date,
  principal: number,
  numProperties: number,
  loanOverrides: GqlLoanPrincipalLateFee | undefined,
  stopSettings: GqlStopSettings,
  isAutomation: boolean,
): number;
export function getPrincipalLateFeeAmount(
  companyFeeSettings:
    | CompanyPrincipalLateFeeSettings
    | GqlCompanyPrincipalLateFeeSettingsFixed
    | GqlCompanyPrincipalLateFeeSettingsPercent,
  maturityDate: Date,
  principal: number,
  numProperties: number,
  loanOverrides: LoanPrincipalLateFee | GqlLoanPrincipalLateFee | undefined,
  stopSettings: StopSettings | GqlStopSettings,
  isAutomation: boolean,
): number {
  /**
   * For client-side, calculate days after maturity to check grace periods
   * For automation (server-side), skip this since getPrincipalLateAutomationTickDate already determined timing
   * and introduced an automation offset to NY time.
   */
  const numDaysAfterMaturityDate = getNumDaysAfterMaturityDate(maturityDate, isAutomation);
  /**
   * If the override flag is present and false or a stop setting is present and true,
   * ignore the company-level setting even if it is enabled
   */
  if (feeDisabledByLoanOverride(loanOverrides) || feeDisabledByStopSettings(stopSettings)) {
    return 0;
  }

  if (loanOverrides) {
    switch (loanOverrides.type) {
      case 'percent':
      case GqlLoanPrincipalLateFeeType.Percent: {
        if (
          isAutomation === false &&
          'percentDays' in loanOverrides &&
          numDaysAfterMaturityDate &&
          numDaysAfterMaturityDate < loanOverrides.percentDays
        ) {
          return 0;
        }
        const percentOverride = loanOverrides as LoanPrincipalLateFeePercent;
        return calculatePercentFee({
          percentPrincipal: percentOverride.percentPrincipal,
          principal,
        });
      }
      case 'fixed':
      case GqlLoanPrincipalLateFeeType.Fixed: {
        if (
          isAutomation === false &&
          'fixedDays' in loanOverrides &&
          numDaysAfterMaturityDate &&
          numDaysAfterMaturityDate < loanOverrides.fixedDays
        ) {
          return 0;
        }
        const fixedOverride = loanOverrides as LoanPrincipalLateFeeFixed;
        return calculateFixedFee({
          fixedAmount: fixedOverride.fixedAmount,
          perProperty: fixedOverride.perProperty,
          numProperties,
        });
      }
      // For type completeness only, but the flag check above will handle this case.
      case 'disabled':
      case GqlLoanPrincipalLateFeeType.Disabled: {
        return 0;
      }
      default:
        assertNever(loanOverrides);
    }
  }

  // This check happens after the loan override because the loan override flag takes precedence
  const { enabled } = companyFeeSettings;
  if (!enabled) return 0;

  switch (companyFeeSettings.type) {
    case 'percent':
    case GqlCompanyPrincipalLateFeeType.Percent: {
      if (
        isAutomation === false &&
        'percentDays' in companyFeeSettings &&
        numDaysAfterMaturityDate &&
        numDaysAfterMaturityDate < companyFeeSettings.percentDays
      ) {
        return 0;
      }

      return calculatePercentFee({
        percentPrincipal: 'percentPrincipal' in companyFeeSettings ? companyFeeSettings.percentPrincipal : 0,
        principal,
      });
    }
    case 'fixed':
    case GqlCompanyPrincipalLateFeeType.Fixed: {
      if (
        isAutomation === false &&
        'fixedDays' in companyFeeSettings &&
        numDaysAfterMaturityDate &&
        numDaysAfterMaturityDate < companyFeeSettings.fixedDays
      ) {
        return 0;
      }
      return calculateFixedFee({
        fixedAmount: 'fixedAmount' in companyFeeSettings ? companyFeeSettings.fixedAmount : 0,
        perProperty: 'perProperty' in companyFeeSettings ? companyFeeSettings.perProperty : false,
        numProperties,
      });
    }
    default:
      assertNever(companyFeeSettings);
  }
}
